import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import { get_api } from '../../../../../redux/api_funcs';
import { get_api_url } from '../../../../../utils/urls';

const formatCurrency = (amount, currency) => {
  if (amount === null || amount === undefined) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

class PayoutDetailsPopup extends Component {
  state = {
    loading: true,
    details: []
  };

  dialogRef = createRef();

  componentDidMount() {
    this.loadDetails();
    document.addEventListener('keydown', this.handleKeyDown);
    // Focus trap - focus the dialog on mount
    if (this.dialogRef.current) {
      this.dialogRef.current.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      this.props.onClose();
    }
  };

  handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      this.props.onClose();
    }
  };

  loadDetails = () => {
    const { type, sessionUuid } = this.props;
    const url = get_api_url(`/v2/cms_user/teachers/sessions/${sessionUuid}/${type}`, null, true);

    get_api(url, true)
      .then((response) => {
        this.setState({
          loading: false,
          details: response.data[type] || response.data || []
        });
      })
      .catch((error) => {
        console.error(`Error fetching ${type} details:`, error);
        this.setState({ loading: false, details: [] });
      });
  };

  getTitle = () => {
    const { type } = this.props;
    switch (type) {
      case 'penalties':
        return 'Penalty Details';
      case 'incentives':
        return 'Incentive Details';
      case 'waivers':
        return 'Waiver Details';
      default:
        return 'Details';
    }
  };

  renderLoadingState = () => (
    <div
      className="flex flex-col items-center justify-center py-8"
      role="status"
      aria-label="Loading details"
    >
      <svg
        className="animate-spin h-8 w-8 text-blue-600 mb-3"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <p className="text-gray-500 text-sm">Loading details...</p>
    </div>
  );

  renderEmptyState = () => {
    const { type } = this.props;
    return (
      <div className="text-center py-8" role="status">
        <svg
          className="w-12 h-12 mx-auto mb-3 text-gray-300"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <p className="text-gray-500">No {type} found for this session.</p>
      </div>
    );
  };

  renderDetails = () => {
    const { type } = this.props;
    const { details } = this.state;

    if (!Array.isArray(details) || details.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <div className="space-y-3" role="list" aria-label={`List of ${type}`}>
        {details.map((item, index) => (
          <article
            key={item.id || index}
            className="bg-gray-50 rounded-lg p-4 border border-gray-200"
            aria-label={`${type.slice(0, -1)} ${index + 1}`}
          >
            {type === 'penalties' && (
              <>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span
                    className="font-semibold text-red-600"
                    aria-label={`Penalty amount ${item.penalty_amount || item.amount}`}
                  >
                    <span aria-hidden="true">−</span>
                    {formatCurrency(item.penalty_amount || item.amount, item.currency)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Reason: </span>
                  <span className="text-gray-700">{item.penalty_reason || item.reason || '-'}</span>
                </div>
                {item.created_at && (
                  <div className="text-xs text-gray-400 mt-2">
                    <time dateTime={item.created_at}>
                      Added: {new Date(item.created_at).toLocaleString()}
                    </time>
                  </div>
                )}
              </>
            )}

            {type === 'incentives' && (
              <>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span
                    className="font-semibold text-green-600"
                    aria-label={`Incentive amount ${item.incentive_amount || item.amount}`}
                  >
                    <span aria-hidden="true">+</span>
                    {formatCurrency(item.incentive_amount || item.amount, item.currency)}
                  </span>
                </div>
                {(item.incentive_type || item.type) && (
                  <div className="text-sm mb-1">
                    <span className="text-gray-500">Type: </span>
                    <span className="text-gray-700">{item.incentive_type || item.type}</span>
                  </div>
                )}
                <div className="text-sm">
                  <span className="text-gray-500">Reason: </span>
                  <span className="text-gray-700">
                    {item.incentive_reason || item.reason || '-'}
                  </span>
                </div>
                {item.created_at && (
                  <div className="text-xs text-gray-400 mt-2">
                    <time dateTime={item.created_at}>
                      Added: {new Date(item.created_at).toLocaleString()}
                    </time>
                  </div>
                )}
              </>
            )}

            {type === 'waivers' && (
              <>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm text-gray-500">Amount</span>
                  <span className="font-semibold text-yellow-600">
                    {formatCurrency(item.waiver_amount || item.amount, item.currency)}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">Reason: </span>
                  <span className="text-gray-700">{item.waiver_reason || item.reason || '-'}</span>
                </div>
                {item.created_at && (
                  <div className="text-xs text-gray-400 mt-2">
                    <time dateTime={item.created_at}>
                      Added: {new Date(item.created_at).toLocaleString()}
                    </time>
                  </div>
                )}
              </>
            )}
          </article>
        ))}
      </div>
    );
  };

  render() {
    const { onClose } = this.props;
    const { loading } = this.state;

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={this.handleBackdropClick}
        role="presentation"
      >
        <div
          ref={this.dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="popup-title"
          className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[85vh] flex flex-col focus:outline-none"
          tabIndex={-1}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 id="popup-title" className="text-lg font-semibold text-gray-900">
              {this.getTitle()}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close dialog"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto flex-1">
            {loading ? this.renderLoadingState() : this.renderDetails()}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-4 py-2.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }
}

PayoutDetailsPopup.propTypes = {
  type: PropTypes.oneOf(['penalties', 'incentives', 'waivers']).isRequired,
  sessionUuid: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PayoutDetailsPopup;
